import { useEffect, useState } from "react";
import Icon from "../../components/Icon";
import LoadingBlock from "../../components/LoadingBlock";
import EmptyState from "../../components/EmptyState";
import Drawer from "../../components/Drawer";
import Pagination from "../../components/Pagination";
import { useApi } from "../../hooks/useApi";
import { listAdmins, createAdmin, updateAdmin, updateAdminStatus, resetAdminPassword, deleteAdmin } from "../../api/adminService";
import { paginatedRows, paginatedMeta } from "../../utils/pagination";
import { formatDate, initials, serialNumber } from "../../utils/formatters";
import { showApiError, showToast } from "../../utils/toast";
import { showCredentials } from "../../utils/credentials";
import { useAuth } from "../../contexts/AuthContext";

// Manage the admin roster: list, onboard a new admin (the backend generates a
// username + one-time temp password), reset passwords, and toggle status to
// block / re-enable logins. An admin can never disable themselves (the
// backend enforces it, but we also hide the toggle on the row that matches
// the logged-in user).
export default function AdminsPage() {
  const { user: me } = useAuth();
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // all | active | inactive
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editAdmin, setEditAdmin] = useState(null);
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 250);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => { setPage(1); }, [debouncedSearch, statusFilter, perPage]);

  const admins = useApi(
    () => {
      const params = { page, per_page: perPage };
      if (debouncedSearch) params.search = debouncedSearch;
      if (statusFilter === "active") params.status = 1;
      else if (statusFilter === "inactive") params.status = 0;
      return listAdmins(params);
    },
    [page, perPage, debouncedSearch, statusFilter],
  );
  const rows = paginatedRows(admins.data);
  const meta = paginatedMeta(admins.data);

  const toggleStatus = async (admin) => {
    const next = !admin.status;
    if (!next && !window.confirm(
      `Disable ${admin.name}? They'll be logged out of every device and won't be able to sign in until you re-enable them.`,
    )) return;

    setBusyId(admin.id);
    try {
      await updateAdminStatus(admin.id, next);
      showToast("success", next ? "Admin enabled" : "Admin disabled");
      admins.reload();
    } catch (err) {
      showApiError(err);
    } finally {
      setBusyId(null);
    }
  };

  const resetPassword = async (admin) => {
    if (!window.confirm(
      `Reset ${admin.name}'s password? They'll be logged out everywhere and must use the new temp password.`,
    )) return;

    setBusyId(admin.id);
    try {
      const res = await resetAdminPassword(admin.id);
      showCredentials(res);
      admins.reload();
    } catch (err) {
      showApiError(err);
    } finally {
      setBusyId(null);
    }
  };

  const removeAdmin = async (admin) => {
    if (!window.confirm(
      `Permanently delete ${admin.name}? Their account, active sessions, and personal todos go away. This cannot be undone — use Disable instead if you just want to temporarily block their login.`,
    )) return;

    setBusyId(admin.id);
    try {
      await deleteAdmin(admin.id);
      showToast("success", "Admin deleted");
      admins.reload();
    } catch (err) {
      showApiError(err);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-title">Admins</h1>
          <div className="page-sub">{meta?.total ?? rows.length} admin{(meta?.total ?? rows.length) === 1 ? "" : "s"}</div>
        </div>
        <div className="page-actions">
          <button className="btn btn-primary" onClick={() => setDrawerOpen(true)}>
            <Icon name="plus" size={14} /> Add admin
          </button>
        </div>
      </div>

      <div className="filter-bar">
        {[
          { id: "all", label: "All" },
          { id: "active", label: "Active" },
          { id: "inactive", label: "Disabled" },
        ].map((f) => (
          <button key={f.id} className={`chip ${statusFilter === f.id ? "active" : ""}`} onClick={() => setStatusFilter(f.id)}>
            {f.label}
          </button>
        ))}
        <div style={{ position: "relative", flex: 1, maxWidth: 360, marginLeft: "auto" }}>
          <Icon
            name="search"
            size={13}
            style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "var(--ink-400)", pointerEvents: "none" }}
          />
          <input
            className="input"
            style={{ paddingLeft: 30 }}
            placeholder="Search by name or phone…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      <div className="card">
        <div className="card-body no-pad">
          {admins.loading ? (
            <div style={{ padding: 16 }}><LoadingBlock rows={5} /></div>
          ) : rows.length === 0 ? (
            <EmptyState
              icon="user"
              title="No admins match"
              hint={debouncedSearch ? "Try a different search." : "Add your first admin."}
            />
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th style={{ width: 50 }} className="t-num">#</th>
                  <th>Name</th>
                  <th>Phone</th>
                  <th>Status</th>
                  <th>Onboarded</th>
                  <th style={{ width: 140 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((a, i) => {
                  const isSelf = Number(a.id) === Number(me?.id);
                  return (
                    <tr key={a.id}>
                      <td className="t-num t-muted">{serialNumber(meta, i)}</td>
                      <td>
                        <div className="row gap-sm">
                          <div className="avatar avatar-sm" style={{ background: `hsl(${(a.id * 47) % 360} 60% 55%)` }}>
                            {initials(a.name)}
                          </div>
                          <div>
                            <div className="t-primary">{a.name} {isSelf && <span className="t-muted text-xs">(you)</span>}</div>
                            {a.username && <div className="t-muted text-xs t-mono">{a.username}</div>}
                          </div>
                        </div>
                      </td>
                      <td className="t-mono">{a.phone}</td>
                      <td>
                        {a.status ? (
                          <span className="badge badge-success">Active</span>
                        ) : (
                          <span className="badge badge-neutral">Disabled</span>
                        )}
                      </td>
                      <td className="t-muted">{formatDate(a.created_at)}</td>
                      <td>
                        <div className="row gap-sm" style={{ justifyContent: "flex-end" }}>
                          <button
                            className="icon-btn"
                            onClick={() => setEditAdmin(a)}
                            disabled={busyId === a.id}
                            title="Edit name / username"
                          >
                            <Icon name="edit" size={13} />
                          </button>
                          {isSelf ? (
                            <span className="t-muted text-xs" title="You can't disable or delete yourself — ask another admin">
                              Self
                            </span>
                          ) : (
                            <>
                              {a.status ? (
                                <button
                                  className="btn btn-secondary btn-sm"
                                  style={{ color: "var(--danger)" }}
                                  onClick={() => toggleStatus(a)}
                                  disabled={busyId === a.id}
                                  title="Disable login + revoke active sessions"
                                >
                                  <Icon name="log-out" size={13} /> {busyId === a.id ? "Disabling…" : "Disable"}
                                </button>
                              ) : (
                                <button
                                  className="btn btn-secondary btn-sm"
                                  onClick={() => toggleStatus(a)}
                                  disabled={busyId === a.id}
                                  title="Re-enable login"
                                >
                                  <Icon name="check" size={13} /> {busyId === a.id ? "Enabling…" : "Enable"}
                                </button>
                              )}
                              <button
                                className="icon-btn"
                                onClick={() => resetPassword(a)}
                                disabled={busyId === a.id}
                                title="Reset password (issues a one-time temp password)"
                              >
                                <Icon name="key" size={13} />
                              </button>
                              <button
                                className="icon-btn"
                                style={{ color: "var(--danger)" }}
                                onClick={() => removeAdmin(a)}
                                disabled={busyId === a.id}
                                title="Permanently delete this admin"
                              >
                                <Icon name="trash" size={13} />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          <Pagination
            meta={meta}
            page={page}
            onPageChange={setPage}
            perPage={perPage}
            onPerPageChange={setPerPage}
            disabled={admins.loading}
          />
        </div>
      </div>

      <CreateAdminDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onCreated={() => { setDrawerOpen(false); admins.reload(); }}
      />

      <EditAdminDrawer
        admin={editAdmin}
        onClose={() => setEditAdmin(null)}
        onSaved={() => { setEditAdmin(null); admins.reload(); }}
      />
    </>
  );
}

function CreateAdminDrawer({ open, onClose, onCreated }) {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [username, setUsername] = useState("");
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!open) return;
    setName("");
    setPhone("");
    setUsername("");
    setErrors({});
  }, [open]);

  const submit = async () => {
    setErrors({});
    setSaving(true);
    try {
      const res = await createAdmin({
        name: name.trim(),
        phone: phone.trim(),
        username: username.trim() || undefined,
      });
      if (!showCredentials(res)) showToast("success", "Admin created");
      onCreated?.();
    } catch (err) {
      if (err?.data?.errors) setErrors(err.data.errors);
      showApiError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="New admin"
      subtitle="A username and one-time temp password are generated for them."
      footer={
        <>
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button
            className="btn btn-primary"
            onClick={submit}
            disabled={saving || !name.trim() || !phone.trim()}
          >
            {saving ? "Creating…" : "Create admin"}
          </button>
        </>
      }
    >
      <div className="col gap-md">
        <div className="field">
          <label className="field-label">Name</label>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Priya Sharma" />
          {errors.name?.[0] && <div className="field-error">{errors.name[0]}</div>}
        </div>
        <div className="field">
          <label className="field-label">Phone</label>
          <input className="input" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+919876543210" />
          <div className="field-hint">Contact number. Country code optional (defaults to +91).</div>
          {errors.phone?.[0] && <div className="field-error">{errors.phone[0]}</div>}
        </div>
        <div className="field">
          <label className="field-label">Username</label>
          <input
            className="input"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="Leave blank to auto-generate from phone"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
          />
          <div className="field-hint">Login username. Lowercase letters, digits, and . _ - only.</div>
          {errors.username?.[0] && <div className="field-error">{errors.username[0]}</div>}
        </div>
      </div>
    </Drawer>
  );
}

function EditAdminDrawer({ admin, onClose, onSaved }) {
  const open = !!admin;
  const [name, setName] = useState("");
  const [username, setUsername] = useState("");
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!admin) return;
    setName(admin.name || "");
    setUsername(admin.username || "");
    setErrors({});
  }, [admin]);

  const submit = async () => {
    setErrors({});
    setSaving(true);
    try {
      await updateAdmin(admin.id, { name: name.trim(), username: username.trim() });
      showToast("success", "Admin updated");
      onSaved?.();
    } catch (err) {
      if (err?.data?.errors) setErrors(err.data.errors);
      showApiError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Edit admin"
      subtitle="Change the display name or login username."
      footer={
        <>
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={saving || !name.trim() || !username.trim()}>
            {saving ? "Saving…" : "Save changes"}
          </button>
        </>
      }
    >
      <div className="col gap-md">
        <div className="field">
          <label className="field-label">Name</label>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
          {errors.name?.[0] && <div className="field-error">{errors.name[0]}</div>}
        </div>
        <div className="field">
          <label className="field-label">Username</label>
          <input
            className="input"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
          />
          <div className="field-hint">Login username. Lowercase letters, digits, and . _ - only.</div>
          {errors.username?.[0] && <div className="field-error">{errors.username[0]}</div>}
        </div>
      </div>
    </Drawer>
  );
}
